package diana.soleil.design.iraniancanadienbazzar.feature.gems

import androidx.compose.foundation.background
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.PaddingValues
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.navigationBarsPadding
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.statusBarsPadding
import androidx.compose.foundation.layout.widthIn
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.lazy.items
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.verticalScroll
import androidx.compose.foundation.layout.Box
import androidx.compose.material3.SnackbarHost
import androidx.compose.material3.SnackbarHostState
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.platform.LocalClipboardManager
import androidx.compose.ui.text.AnnotatedString
import androidx.lifecycle.compose.collectAsStateWithLifecycle
import diana.soleil.design.iraniancanadienbazzar.feature.gems.resources.gems_copied
import kotlinx.coroutines.launch
import diana.soleil.design.iraniancanadienbazzar.core.designsystem.component.AppCard
import diana.soleil.design.iraniancanadienbazzar.core.designsystem.component.AppChip
import diana.soleil.design.iraniancanadienbazzar.core.designsystem.component.EmptyState
import diana.soleil.design.iraniancanadienbazzar.core.designsystem.component.LoadingState
import diana.soleil.design.iraniancanadienbazzar.core.designsystem.theme.AppTheme
import diana.soleil.design.iraniancanadienbazzar.core.common.ShareService
import diana.soleil.design.iraniancanadienbazzar.core.data.ShareConfig
import diana.soleil.design.iraniancanadienbazzar.core.model.Story
import diana.soleil.design.iraniancanadienbazzar.core.model.StoryRegion
import diana.soleil.design.iraniancanadienbazzar.feature.gems.resources.Res
import diana.soleil.design.iraniancanadienbazzar.feature.gems.resources.gems_error
import diana.soleil.design.iraniancanadienbazzar.feature.gems.resources.gems_region_azerbaijan
import diana.soleil.design.iraniancanadienbazzar.feature.gems.resources.gems_region_fars
import diana.soleil.design.iraniancanadienbazzar.feature.gems.resources.gems_region_gilan
import diana.soleil.design.iraniancanadienbazzar.feature.gems.resources.gems_region_isfahan
import diana.soleil.design.iraniancanadienbazzar.feature.gems.resources.gems_region_khorasan
import diana.soleil.design.iraniancanadienbazzar.feature.gems.resources.gems_region_khuzestan
import diana.soleil.design.iraniancanadienbazzar.feature.gems.resources.gems_region_kurdistan
import diana.soleil.design.iraniancanadienbazzar.feature.gems.resources.gems_region_mazandaran
import diana.soleil.design.iraniancanadienbazzar.feature.gems.resources.gems_region_sistan
import diana.soleil.design.iraniancanadienbazzar.feature.gems.resources.gems_story_region_iran
import diana.soleil.design.iraniancanadienbazzar.feature.gems.resources.gems_stories_empty_desc
import diana.soleil.design.iraniancanadienbazzar.feature.gems.resources.gems_stories_empty_title
import diana.soleil.design.iraniancanadienbazzar.feature.gems.resources.gems_stories_title
import diana.soleil.design.iraniancanadienbazzar.feature.gems.resources.gems_region_all
import diana.soleil.design.iraniancanadienbazzar.feature.gems.resources.gems_share_message
import androidx.compose.foundation.lazy.LazyRow
import diana.soleil.design.iraniancanadienbazzar.feature.gems.resources.gems_tale_moral_label
import org.jetbrains.compose.resources.stringResource
import org.koin.compose.koinInject
import org.koin.compose.viewmodel.koinViewModel
import org.koin.core.parameter.parametersOf

/** A blank line (with any stray spaces on it) separates paragraphs in the authored body. */
private val PARAGRAPH_BREAK = Regex("\\n\\s*\\n")

/** Localized label for a story's region — the chip on the card and the detail header. */
@Composable
internal fun StoryRegion.label(): String = stringResource(
    when (this) {
        StoryRegion.Gilan -> Res.string.gems_region_gilan
        StoryRegion.Mazandaran -> Res.string.gems_region_mazandaran
        StoryRegion.Azerbaijan -> Res.string.gems_region_azerbaijan
        StoryRegion.Kurdistan -> Res.string.gems_region_kurdistan
        StoryRegion.Khorasan -> Res.string.gems_region_khorasan
        StoryRegion.Fars -> Res.string.gems_region_fars
        StoryRegion.Isfahan -> Res.string.gems_region_isfahan
        StoryRegion.Sistan -> Res.string.gems_region_sistan
        StoryRegion.Khuzestan -> Res.string.gems_region_khuzestan
        // The recipe browser's `gems_region_other` means "other regions" in a filter list; a story
        // with no specific province is simply from Iran, so it gets its own label.
        StoryRegion.Other -> Res.string.gems_story_region_iran
    },
)

/** Stateful entry for the short-stories browser (`/gems/stories`). */
@Composable
fun StoriesRoute(
    onBack: () -> Unit,
    onOpenStory: (storyId: String) -> Unit,
    modifier: Modifier = Modifier,
    viewModel: StoriesViewModel = koinViewModel(),
) {
    val state by viewModel.state.collectAsStateWithLifecycle()
    StoriesScreen(
        state = state,
        onBack = onBack,
        onOpenStory = onOpenStory,
        onSelectRegion = viewModel::selectRegion,
        modifier = modifier,
    )
}

/** A region chip rail, then a card per story: region + reading time, title, and the one-line hook. */
@Composable
fun StoriesScreen(
    state: StoriesUiState,
    onBack: () -> Unit,
    onOpenStory: (storyId: String) -> Unit,
    onSelectRegion: (StoryRegion?) -> Unit = {},
    modifier: Modifier = Modifier,
) {
    val lang = AppTheme.language
    Column(
        modifier = modifier.fillMaxSize().background(AppTheme.colors.background)
            .statusBarsPadding().navigationBarsPadding(),
        horizontalAlignment = Alignment.CenterHorizontally,
    ) {
        GemsSubTopBar(title = stringResource(Res.string.gems_stories_title), onBack = onBack)
        // Region rail — only shown once there is more than one region to choose between, and only
        // offering regions that have a story (same rule as the recipe browser).
        if (state.availableRegions.size > 1) {
            LazyRow(
                modifier = Modifier.fillMaxWidth(),
                horizontalArrangement = Arrangement.spacedBy(AppTheme.spacing.sm),
                contentPadding = PaddingValues(horizontal = AppTheme.spacing.lg, vertical = AppTheme.spacing.sm),
            ) {
                item("all") {
                    AppChip(
                        label = stringResource(Res.string.gems_region_all),
                        selected = state.selectedRegion == null,
                        onClick = { onSelectRegion(null) },
                    )
                }
                items(state.availableRegions, key = { it.name }) { region ->
                    AppChip(
                        label = region.label(),
                        selected = state.selectedRegion == region,
                        onClick = { onSelectRegion(region) },
                    )
                }
            }
        }
        when {
            state.loading -> LoadingState()
            state.error -> EmptyState(title = stringResource(Res.string.gems_error))
            state.stories.isEmpty() -> EmptyState(
                title = stringResource(Res.string.gems_stories_empty_title),
                description = stringResource(Res.string.gems_stories_empty_desc),
            )
            else -> LazyColumn(
                modifier = Modifier.widthIn(max = AppTheme.sizes.contentMaxWidthCompact).fillMaxWidth(),
                contentPadding = PaddingValues(AppTheme.spacing.lg),
                verticalArrangement = Arrangement.spacedBy(AppTheme.spacing.md),
            ) {
                items(state.stories, key = { it.id }) { story ->
                    AppCard(modifier = Modifier.fillMaxWidth(), onClick = { onOpenStory(story.id) }) {
                        Column(
                            modifier = Modifier.fillMaxWidth().padding(AppTheme.spacing.md),
                            verticalArrangement = Arrangement.spacedBy(AppTheme.spacing.sm),
                        ) {
                            ReadingMetaRow(
                                start = { AppChip(label = story.region.label(), selected = false) },
                                end = readTimeLabel(story.body.resolve(lang), lang),
                            )
                            Text(
                                text = story.title.resolve(lang),
                                style = AppTheme.typography.headlineMd,
                                color = AppTheme.colors.onSurface,
                            )
                            Text(
                                text = story.summary.resolve(lang),
                                style = AppTheme.typography.bodyMd,
                                color = AppTheme.colors.onSurfaceVariant,
                            )
                        }
                    }
                }
            }
        }
    }
}

/** Stateful entry for one story (`/gems/stories/{storyId}`). */
@Composable
fun StoryDetailRoute(
    storyId: String,
    onBack: () -> Unit,
    onRequireSignIn: () -> Unit,
    modifier: Modifier = Modifier,
    viewModel: StoryDetailViewModel = koinViewModel { parametersOf(storyId) },
) {
    val state by viewModel.state.collectAsStateWithLifecycle()
    val shareService = koinInject<ShareService>()
    val shareConfig = koinInject<ShareConfig>()
    val lang = AppTheme.language
    LaunchedEffect(state.signInRequired) {
        if (state.signInRequired) { viewModel.consumeSignInGate(); onRequireSignIn() }
    }
    LaunchedEffect(state.reportStatus) {
        if (state.reportStatus == GemReportStatus.SignInRequired) { viewModel.resetReport(); onRequireSignIn() }
    }
    val shareTemplate = stringResource(Res.string.gems_share_message)
    StoryDetailScreen(
        state = state,
        onBack = onBack,
        onToggleLike = viewModel::toggleLike,
        onToggleSave = viewModel::toggleSave,
        onShare = { story ->
            shareService.share(
                shareTemplate
                    .replace("%1\$s", story.title.resolve(lang))
                    .replace("%2\$s", shareConfig.storyLink(story.id)),
            )
        },
        onReport = viewModel::report,
        onResetReport = viewModel::resetReport,
        modifier = modifier,
    )
}

/** The reading view: region chip → actions → the retelling → its one-line moral. */
@Composable
fun StoryDetailScreen(
    state: StoryDetailUiState,
    onBack: () -> Unit,
    onToggleLike: () -> Unit,
    onToggleSave: () -> Unit,
    onShare: (Story) -> Unit,
    onReport: (reason: String) -> Unit,
    onResetReport: () -> Unit,
    modifier: Modifier = Modifier,
) {
    val lang = AppTheme.language
    val clipboard = LocalClipboardManager.current
    val snackbarHostState = remember { SnackbarHostState() }
    val scope = rememberCoroutineScope()
    val copiedMessage = stringResource(Res.string.gems_copied)
    var showReport by remember { mutableStateOf(false) }

    Box(modifier = modifier.fillMaxSize().background(AppTheme.colors.background)) {
        Column(
            modifier = Modifier.fillMaxSize().statusBarsPadding().navigationBarsPadding(),
            horizontalAlignment = Alignment.CenterHorizontally,
        ) {
            GemsSubTopBar(
                title = state.story?.title?.resolve(lang) ?: stringResource(Res.string.gems_stories_title),
                onBack = onBack,
            )
            when {
                state.loading -> LoadingState()
                state.error || state.story == null -> EmptyState(title = stringResource(Res.string.gems_error))
                else -> {
                    val story = state.story
                    Column(
                        modifier = Modifier
                            .widthIn(max = AppTheme.sizes.contentMaxWidthCompact)
                            .fillMaxWidth()
                            .verticalScroll(rememberScrollState())
                            .padding(AppTheme.spacing.lg),
                        verticalArrangement = Arrangement.spacedBy(AppTheme.spacing.md),
                    ) {
                        val body = story.body.resolve(lang)
                        ReadingMetaRow(
                            start = { AppChip(label = story.region.label(), selected = false) },
                            end = readTimeLabel(body, lang),
                        )
                        // Paragraphs as separate blocks with real spacing, in the reading scale —
                        // a folk tale is read, not skimmed, and bodyMd with embedded blank lines
                        // looked like a form field.
                        Column(verticalArrangement = Arrangement.spacedBy(AppTheme.spacing.md)) {
                            body.split(PARAGRAPH_BREAK).map { it.trim() }.filter { it.isNotEmpty() }.forEach { paragraph ->
                                Text(
                                    text = paragraph,
                                    style = AppTheme.typography.bodyLg,
                                    color = AppTheme.colors.onSurface,
                                )
                            }
                        }
                        story.moral?.resolve(lang)?.takeIf { it.isNotBlank() }?.let { moral ->
                            Column(
                                modifier = Modifier
                                    .fillMaxWidth()
                                    .background(AppTheme.colors.primaryContainer, AppTheme.shapes.card)
                                    .padding(AppTheme.spacing.md),
                                verticalArrangement = Arrangement.spacedBy(AppTheme.spacing.xs),
                            ) {
                                Text(
                                    text = stringResource(Res.string.gems_tale_moral_label),
                                    style = AppTheme.typography.labelSm,
                                    color = AppTheme.colors.onPrimaryContainer,
                                )
                                Text(
                                    text = moral,
                                    style = AppTheme.typography.bodyMd,
                                    color = AppTheme.colors.onPrimary,
                                )
                            }
                        }
                        // Actions after the story: read first, then like, save or share it.
                        GemDetailRail(
                            likeCount = story.likeCount,
                            liked = state.liked,
                            saved = state.saved,
                            onToggleLike = onToggleLike,
                            onToggleSave = onToggleSave,
                            onCopy = {
                                clipboard.setText(AnnotatedString(body))
                                scope.launch { snackbarHostState.showSnackbar(copiedMessage) }
                            },
                            onShare = { onShare(story) },
                            onReport = { showReport = true },
                        )
                    }
                }
            }
        }
        SnackbarHost(hostState = snackbarHostState, modifier = Modifier.align(Alignment.BottomCenter))
    }

    if (showReport) {
        GemReportSheet(
            status = state.reportStatus,
            onSubmit = onReport,
            onDismiss = { showReport = false; onResetReport() },
        )
    }
}
